import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { usePanelStore } from '../store/panel-store.js';
import { PanelButton } from '../components/PanelButton.js';
import { HardwarePtt } from '../components/HardwarePtt.js';
import { ReplyButton } from '../components/ReplyButton.js';
import { EmptySlot } from '../components/EmptySlot.js';
import { BrandLogo } from '../components/BrandLogo.js';
import {
  ensurePanelConnected,
  unlockAudio,
  setAudioPlaybackListener,
  getActiveIncomingPanels,
  getMicError,
  setMemberPartylines,
} from '../lib/livekit.js';
import { Wifi, WifiOff, Volume2, LayoutGrid, List as ListIcon, ArrowUpDown, Check, ShieldCheck } from 'lucide-react';
import { td, debugEnabled } from '../lib/touch-debug.js';
import { loadPanelPrefs, savePanelPrefs, applyOrder, type PanelViewMode } from '../lib/panel-prefs.js';
import { AdminBar } from '../components/AdminBar.js';

export function PanelView(): JSX.Element {
  const { panelId } = useParams<{ panelId: string }>();
  const config = usePanelStore((s) => s.config);
  const configLoaded = usePanelStore((s) => s.configLoaded);
  const online = usePanelStore((s) => s.online);
  const pressed = usePanelStore((s) => s.pressed);
  const remoteActive = usePanelStore((s) => s.remoteActive);
  const remoteVolume = usePanelStore((s) => s.remoteVolume);
  const hwTargetButtonId = usePanelStore((s) => s.hwTargetButtonId);
  const setPressed = usePanelStore((s) => s.setPressed);
  const bind = usePanelStore((s) => s.bind);
  const loadConfig = usePanelStore((s) => s.loadConfig);

  useEffect(() => {
    if (!panelId) return;
    bind(panelId);
    loadConfig().catch((err: Error) => console.warn(err.message));
    void ensurePanelConnected(panelId);
  }, [panelId, bind, loadConfig]);

  const [audioBlocked, setAudioBlocked] = useState(false);
  // Touch-Diagnose (?debug=1): zwingt Re-Render, damit das Overlay aktuell ist.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!debugEnabled()) return;
    const t = window.setInterval(() => forceTick((n) => n + 1), 150);
    return () => window.clearInterval(t);
  }, []);
  // Panel-IDs, die uns GERADE ansprechen → zugehörige Buttons leuchten orange.
  const [incomingPanels, setIncomingPanels] = useState<string[]>([]);
  const [micError, setMicError] = useState<string | null>(null);

  // „Zuletzt gesprochen": merkt sich, wer als Letztes ZU SPRECHEN BEGONNEN hat,
  // und bleibt stehen, bis jemand Neues spricht. REINE ANZEIGE – kein Audio.
  const [lastCaller, setLastCaller] = useState<string | null>(null);
  const prevIncoming = useRef<Set<string>>(new Set());
  useEffect(() => {
    const added = incomingPanels.filter((p) => !prevIncoming.current.has(p));
    if (added.length > 0) setLastCaller(added[added.length - 1] ?? null);
    prevIncoming.current = new Set(incomingPanels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingPanels.join(',')]);


  // Audio-Wiedergabe bei JEDER Nutzergeste entsperren – auch für reine
  // Zuhörer-Panels, die selbst nie einen Button drücken. Ohne diese Geste
  // blockieren mobile Browser das Abspielen eingehender Tracks dauerhaft.
  useEffect(() => {
    const unlock = (): void => unlockAudio();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('touchstart', unlock, { passive: true });
    setAudioPlaybackListener((ok) => setAudioBlocked(!ok));
    const t = window.setInterval(() => {
      setIncomingPanels(getActiveIncomingPanels());
      setMicError(getMicError());
    }, 400);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
      setAudioPlaybackListener(null);
      window.clearInterval(t);
    };
  }, []);

  const panel = useMemo(
    () => (config && panelId ? config.panels.find((p) => p.id === panelId) : null) ?? null,
    [config, panelId],
  );

  // Mitglied-Partylines (dauerhaftes Mithören) aus den Port-Gruppen-Tasten
  // dieses Panels ableiten und der Engine melden. So hört man die Gruppe immer;
  // die Taste steuert nur das Senden.
  useEffect(() => {
    if (!panel || !config) {
      setMemberPartylines([]);
      return;
    }
    const ids = panel.layout
      .map((s) => (s.buttonId ? config.buttons.find((b) => b.id === s.buttonId) : null))
      .filter((b): b is NonNullable<typeof b> => b?.type === 'partyline')
      .map((b) => (b.type === 'partyline' ? b.partylineId : undefined))
      .filter((id): id is string => !!id);
    setMemberPartylines(ids);
  }, [panel, config]);

  // ----- Ansichtsvariante + persönliche Reihenfolge (lokal, pro Gerät) -----
  const [viewMode, setViewMode] = useState<PanelViewMode>('list');
  const [order, setOrder] = useState<number[]>([]);
  const [editMode, setEditMode] = useState(false);
  const orderRef = useRef<number[]>([]);
  useEffect(() => {
    orderRef.current = order;
  }, [order]);
  // Beim Panel-Wechsel gespeicherte Präferenz laden.
  useEffect(() => {
    if (!panelId) return;
    const p = loadPanelPrefs(panelId);
    setViewMode(p.viewMode);
    setOrder(p.order);
    setEditMode(false);
  }, [panelId]);
  // Änderungen sofort persistent sichern.
  useEffect(() => {
    if (panelId) savePanelPrefs(panelId, { viewMode, order });
  }, [panelId, viewMode, order]);
  // Drag-Reorder-Zustand (Position der gerade gezogenen Taste).
  const draggingPosRef = useRef<number | null>(null);
  const [draggingPos, setDraggingPos] = useState<number | null>(null);
  // Admin-Overlay (nur für Admin-Panels).
  const [showAdmin, setShowAdmin] = useState(false);

  if (!panelId) {
    return <Fallback message="Keine Port-ID in der URL." />;
  }  if (!configLoaded) {
    return <Fallback message="Verbinde…" />;
  }  if (config === null) {
    return (
      <Fallback message={'Es wurde noch keine Konfiguration live geschaltet. Bitte im Admin (Port 5173) zuerst „Push“ drücken.'}>
        <Link to="/" className="text-accent text-sm underline">
          Zur Auswahl
        </Link>
      </Fallback>
    );
  }
  // Quellen-Panels haben keine Tasten → eigene Einspiel-Seite.
  if (panel && panel.kind === 'source') {
    return <Navigate to={`/source/${panel.id}`} replace />;
  }

  if (!panel) {
    return (
      <Fallback message={'Dieser Port existiert in der Live-Config nicht (mehr). Falls eben im Admin angelegt: erst „Push“ drücken.'}>
        <Link to="/" className="text-accent text-sm underline">
          Zur Auswahl
        </Link>
      </Fallback>
    );
  }

  // Ziel-Panels, die wir gerade per Listen-Button mithören. Die zugehörige
  // „Call to <Panel>"-Taste auf DIESEM Panel leuchtet dann mit (Sprech-Rückkanal).
  const listenedTargets = new Set<string>();
  for (const slot of panel.layout) {
    const b = slot.buttonId ? config.buttons.find((x) => x.id === slot.buttonId) : null;
    if (b && b.type === 'listen' && b.targetPanelId && pressed.has(b.id)) {
      listenedTargets.add(b.targetPanelId);
    }
  }

  // Belegte Tasten dieses Panels (Layout-Reihenfolge) – Zielauswahl für den
  // optionalen Hardware-Funktaster.
  const pressableButtons = panel.layout
    .map((slot) => (slot.buttonId ? config.buttons.find((b) => b.id === slot.buttonId) : null))
    .filter((b): b is NonNullable<typeof b> => !!b);

  // Slots in persönlicher Reihenfolge (gilt für beide Ansichten).
  const orderedSlots = applyOrder(panel.layout, order);

  // Drag&Drop-Reorder: Taste „aufnehmen", über eine andere ziehen → neue
  // Position. Läuft über Pointer-Events (Maus UND Touch); die Ziel-Kachel wird
  // per elementFromPoint bestimmt. Nur im Sortier-Modus aktiv.
  const beginDrag = (e: React.PointerEvent<HTMLDivElement>, pos: number): void => {
    if (!editMode) return;
    e.preventDefault();
    draggingPosRef.current = pos;
    setDraggingPos(pos);
    if (typeof navigator.vibrate === 'function') navigator.vibrate(15);
    const onMove = (ev: PointerEvent): void => {
      const dp = draggingPosRef.current;
      if (dp == null) return;
      const target = document
        .elementFromPoint(ev.clientX, ev.clientY)
        ?.closest('[data-slot-pos]') as HTMLElement | null;
      if (!target) return;
      const targetPos = Number(target.dataset.slotPos);
      if (Number.isNaN(targetPos) || targetPos === dp) return;
      const base = applyOrder(panel.layout, orderRef.current).map((s) => s.position);
      const from = base.indexOf(dp);
      const to = base.indexOf(targetPos);
      if (from < 0 || to < 0) return;
      base.splice(from, 1);
      base.splice(to, 0, dp);
      orderRef.current = base;
      setOrder(base);
    };
    const onUp = (): void => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      draggingPosRef.current = null;
      setDraggingPos(null);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };

  return (
    <div className="min-h-full flex flex-col">
      {debugEnabled() ? (
        <div className="fixed top-1 left-1 z-50 rounded bg-black/85 px-2 py-1 font-mono text-[10px] leading-tight text-lime-300 pointer-events-none max-w-[95vw]">
          <div>
            start:{td.start} move:{td.move} end:{td.end} cancel:{td.cancel} ptr:{td.ptr} dx:
            {td.lastDx} vm:{td.vm} vol:{td.vol}
          </div>
          <div className="text-cyan-300 break-words">
            gehört ({incomingPanels.length}):{' '}
            {incomingPanels
              .map((id) => config?.panels.find((p) => p.id === id)?.name ?? id)
              .join(', ') || '–'}
          </div>
        </div>
      ) : null}
      <header className="relative flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-gradient-to-b from-surface to-bg">
        {/* Akzent-Linie oben (LIVEPULS magenta → cyan). */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-0.5"
          style={{ backgroundImage: 'linear-gradient(90deg, #ff0099 0%, #00f2ff 100%)' }}
        />

        {/* Links: kleines LIVEPULS-Logo, klickbar → zurück zur Auswahl. */}
        <Link to="/" className="shrink-0" aria-label="Zur Auswahl">
          <BrandLogo size="sm" />
        </Link>

        {/* Mitte: Panel-Name, mittig und prominent. */}
        <h1 className="absolute left-1/2 -translate-x-1/2 max-w-[50%] truncate text-center text-base font-semibold tracking-tight text-text">
          {panel.name}
        </h1>

        {/* Rechts: Ansicht/Sortieren + optionaler Funktaster + Status-Pille. */}
        <div className="shrink-0 flex items-center gap-2">
          {/* Ansichtsvariante umschalten (Liste ⇄ kompakte Kästchen). */}
          <button
            type="button"
            onClick={() => setViewMode((v) => (v === 'list' ? 'grid' : 'list'))}
            className="rounded-full border border-border bg-surface/60 p-1.5 text-text-dim hover:text-text"
            title={viewMode === 'list' ? 'Kompakte Kästchen-Ansicht' : 'Normale Listen-Ansicht'}
            aria-label="Ansicht wechseln"
          >
            {viewMode === 'list' ? <LayoutGrid size={16} /> : <ListIcon size={16} />}
          </button>
          {/* Sortier-/Edit-Modus: Tasten werden inert und lassen sich verschieben. */}
          <button
            type="button"
            onClick={() => setEditMode((e) => !e)}
            className={[
              'rounded-full border p-1.5',
              editMode
                ? 'border-accent/60 bg-accent/15 text-accent'
                : 'border-border bg-surface/60 text-text-dim hover:text-text',
            ].join(' ')}
            title={editMode ? 'Sortieren beenden' : 'Tasten sortieren'}
            aria-label="Tasten sortieren"
          >
            {editMode ? <Check size={16} /> : <ArrowUpDown size={16} />}
          </button>
          {/* Admin-Button: nur für Panels mit Adminrechten. Öffnet die
              Serverauslastungs-Leiste + das eingebettete Admin-Backend. */}
          {panel.admin ? (
            <button
              type="button"
              onClick={() => setShowAdmin(true)}
              className="rounded-full border border-accent/50 bg-accent/10 p-1.5 text-accent hover:bg-accent/20"
              title="Admin: Serverauslastung & Panels bearbeiten"
              aria-label="Admin"
            >
              <ShieldCheck size={16} />
            </button>
          ) : null}
          <HardwarePtt panelId={panelId} buttons={pressableButtons} config={config} />
          <div
            className={[
              'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xxs uppercase tracking-wider',
              online
                ? 'border-ok/40 bg-ok/10 text-ok'
                : 'border-err/40 bg-err/10 text-err',
            ].join(' ')}
          >
            {online ? <Wifi size={13} /> : <WifiOff size={13} />}
            <span>{online ? 'online' : 'offline'}</span>
          </div>
        </div>
      </header>

      {/* Mikrofon-/Sende-Fehler sichtbar machen (für die Diagnose). */}
      {micError ? (
        <div className="w-full px-4 py-2 bg-err text-white text-xs font-semibold break-words">
          🎤 Mic-Fehler: {micError}
        </div>
      ) : null}

      {/* Autoplay-Sperre sichtbar machen: Tippen entsperrt die Wiedergabe. */}
      {audioBlocked ? (
        <button
          type="button"
          onClick={() => unlockAudio()}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-accent text-black font-semibold text-sm"
        >
          <Volume2 size={18} />
          Ton aktivieren (hier tippen)
        </button>
      ) : null}

      {/*
        Layout — zwei Ansichtsvarianten (lokal umschaltbar):
         - 'list' (Standard): mobil eine Spalte (volle Breite, feste Tap-Höhe),
           PC quadratisches 4er-Raster wie bisher.
         - 'grid' (kompakt): kleine Kästchen, 4 nebeneinander (PC 6) → viele auf
           einen Blick.
        Sortier-Modus (editMode): Tasten sind inert und per Drag verschiebbar.
      */}
      {editMode ? (
        <div className="w-full px-4 py-2 bg-accent/15 text-accent text-xs font-semibold flex items-center gap-2 border-b border-accent/30">
          <ArrowUpDown size={14} />
          Sortier-Modus: Tasten gedrückt halten und an die gewünschte Position ziehen. Zum Beenden oben auf ✓ tippen.
        </div>
      ) : null}
      {/* Reine Info-Anzeige ganz oben: wer zuletzt gesprochen hat (KEIN Audio). */}
      <div className="pt-2.5 pl-2.5 pr-16 md:pr-14">
        <ReplyButton
          targetName={
            lastCaller ? config.panels.find((p) => p.id === lastCaller)?.name ?? null : null
          }
          speaking={!!lastCaller && incomingPanels.includes(lastCaller)}
        />
      </div>

      <div
        className={[
          'flex-1 overflow-y-auto py-2.5 pl-2.5 pr-16 md:pr-14 grid gap-2.5 content-start',
          viewMode === 'grid'
            ? 'grid-cols-4 md:grid-cols-6 auto-rows-[4.25rem]'
            : 'grid-cols-1 md:grid-cols-4 auto-rows-[4.75rem] md:auto-rows-fr',
        ].join(' ')}
      >
        {orderedSlots.map((slot) => {
          const btn = slot.buttonId ? config.buttons.find((b) => b.id === slot.buttonId) : null;
          // Button leuchtet orange, wenn er per Admin-Remote aktiv ist ODER
          // wenn uns das per Intercom-Button adressierte Panel gerade anspricht.
          const beingCalled =
            !!btn &&
            ((btn.type === 'intercom' && incomingPanels.includes(btn.targetPanelId)) ||
              (btn.type === 'listen' &&
                !!btn.targetPanelId &&
                incomingPanels.includes(btn.targetPanelId)));
          // Sprech-Rückkanal: passende Call-Taste leuchtet, wenn wir das Ziel mithören.
          const autoTalk =
            !!btn && btn.type === 'intercom' && listenedTargets.has(btn.targetPanelId);
          const isDragging = draggingPos === slot.position;
          return (
            <div
              key={slot.position}
              data-slot-pos={slot.position}
              onPointerDown={editMode ? (e) => beginDrag(e, slot.position) : undefined}
              style={editMode ? { touchAction: 'none' } : undefined}
              className={[
                viewMode === 'grid' ? '' : 'md:aspect-square',
                'transition-transform',
                isDragging ? 'scale-95 opacity-60 z-10 relative' : '',
              ].join(' ')}
            >
              {btn ? (
                <PanelButton
                  button={btn}
                  config={config}
                  pressed={pressed.has(btn.id)}
                  remoteActive={remoteActive.has(btn.id) || autoTalk}
                  remoteVolume={remoteVolume[btn.id]}
                  addressed={beingCalled}
                  hwSelected={hwTargetButtonId === btn.id}
                  editing={editMode}
                  compact={viewMode === 'grid'}
                  panelId={panelId}
                  onPressedChange={(p) => setPressed(btn.id, p)}
                />
              ) : (
                <EmptySlot />
              )}
            </div>
          );
        })}
      </div>

      {/* Admin-Overlay (Serverauslastung + eingebettetes Backend). */}
      {showAdmin ? <AdminBar onClose={() => setShowAdmin(false)} /> : null}
    </div>
  );
}

function Fallback({
  message,
  children,
}: {
  message: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="min-h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-text-dim text-sm">{message}</p>
      {children}
    </div>
  );
}
